import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import {
  normalizeCompanyDetails,
  type CompanyDetailsErrors,
  type CompanyDetailsField,
} from '@shared/utils/giftVoucherBuyer';
import { getApiBaseUrl } from '../api/config';
import { resolveShopTheme, themeCssVars, type ShopBranding, type ShopTheme } from '../theme';
import { useI18n } from '../i18n';
import { formatLongDate, formatMoney } from '../i18n/format';
import type { ShopLegal } from '../legal';
import { AccentButton, ShopFooter, ShopHeader, SmallCaps } from '../components/ShopChrome';
import CompanyDetailsFields from '../components/CompanyDetailsFields';
import { NoVatNote } from '../components/BuyerTypeSection';
import {
  clearFieldError,
  emptyCompanyDetails,
  explainerPath,
  type CompanyDetailsForm,
} from '../components/companyDetailsForm';

/**
 * "Add company details": a buyer who checked out as a private person follows the link in
 * their purchase confirmation email and adds their company's details. The API then issues
 * a new version of the purchase confirmation carrying them and emails it.
 */

type BlockReason = 'already_used' | 'expired' | 'cancelled' | 'not_private' | 'no_document';

interface CompanyDetailsContext {
  allowed: boolean;
  reason: BlockReason | null;
  merchant_name: string | null;
  location_id: string | null;
  document_number: string | null;
  amount: number | null;
  currency: string | null;
  issued_at: string | null;
  buyer_email: string | null;
  merchant_country: string | null;
  locale: string | null;
  explainer_url: string | null;
}

interface ShopVenue {
  location_name?: string;
  location_address?: string;
  location_language?: string;
  branding?: ShopBranding;
  legal?: ShopLegal;
}

const KNOWN_REASONS: BlockReason[] = ['already_used', 'expired', 'cancelled', 'not_private', 'no_document'];

type PageState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error' }
  | { kind: 'blocked'; reason: string }
  | { kind: 'form' }
  | { kind: 'done'; documentNumber: string | null; sentTo: string | null };

const CompanyDetailsPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t, locale, applyVenueLocale } = useI18n();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [context, setContext] = useState<CompanyDetailsContext | null>(null);
  const [venue, setVenue] = useState<ShopVenue | null>(null);
  const [company, setCompany] = useState<CompanyDetailsForm>(() => emptyCompanyDetails());
  const [errors, setErrors] = useState<CompanyDetailsErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const theme = useMemo(() => resolveShopTheme(venue?.branding), [venue?.branding]);
  const endpoint = `${getApiBaseUrl()}/gift-vouchers/public/company-details/${encodeURIComponent(token || '')}`;

  useEffect(() => {
    if (!token) {
      setState({ kind: 'not_found' });
      return;
    }
    let active = true;
    (async () => {
      try {
        const response = await fetch(endpoint, { cache: 'no-store' });
        if (!active) return;
        if (response.status === 404) {
          setState({ kind: 'not_found' });
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = (await response.json()) as CompanyDetailsContext;
        if (!active) return;
        setContext(data);
        // The page speaks the language the confirmation was written in, unless the guest
        // asked for another (?lang= or a remembered choice).
        if (data.locale) applyVenueLocale(data.locale);
        setCompany(
          emptyCompanyDetails({
            country: data.merchant_country || undefined,
            billingEmail: data.buyer_email || undefined,
          })
        );
        setState(data.allowed ? { kind: 'form' } : { kind: 'blocked', reason: data.reason || 'unknown' });

        // The venue's branding, when we can get it. Never blocks the page.
        if (data.location_id) {
          try {
            const shop = await fetch(`${getApiBaseUrl()}/gift-vouchers/shop/${encodeURIComponent(data.location_id)}`);
            if (!shop.ok || !active) return;
            const shopData = (await shop.json()) as ShopVenue;
            if (!active) return;
            setVenue(shopData);
            if (!data.locale) applyVenueLocale(shopData.location_language);
          } catch {
            /* plain paper is fine */
          }
        }
      } catch (error) {
        console.error('Failed to load company details link', error);
        if (active) setState({ kind: 'error' });
      }
    })();
    return () => {
      active = false;
    };
  }, [token, endpoint, applyVenueLocale]);

  const updateField = (field: CompanyDetailsField, value: string) => {
    setCompany((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => clearFieldError(prev, field));
    setFormError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const parsed = normalizeCompanyDetails(company);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      setFormError(t('companyDetails.error_fix'));
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company: parsed.company }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.ok && body?.success) {
        setState({ kind: 'done', documentNumber: body.document_number ?? null, sentTo: body.sent_to ?? null });
        return;
      }
      const code: string | undefined = body?.error?.code;
      if (response.status === 400 && code === 'invalid_company') {
        setErrors(body.error.fields || {});
        setFormError(t('companyDetails.error_fix'));
      } else if (response.status === 404) {
        setState({ kind: 'not_found' });
      } else if (response.status === 409 && code) {
        setState({ kind: 'blocked', reason: code });
      } else {
        setFormError(t('companyDetails.submit_error'));
      }
    } catch {
      setFormError(t('companyDetails.submit_error'));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    document.title = t('companyDetails.page_title');
  }, [t]);

  const merchantName = venue?.location_name || context?.merchant_name || undefined;
  const merchantCountry = context?.merchant_country || undefined;
  const explainerHref = explainerPath(locale, merchantCountry);

  return (
    <div className="flex min-h-screen flex-col" style={themeCssVars(theme)}>
      <ShopHeader
        theme={theme}
        venueName={merchantName}
        onHome={context?.location_id ? () => navigate(`/shop/${context.location_id}`) : undefined}
        onCheckVoucher={() => navigate('/voucher')}
      />

      <main className="mx-auto w-full max-w-[720px] flex-1 px-5 py-12 sm:px-10 sm:py-16">
        {state.kind === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-16">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
            <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>{t('common.loading')}</p>
          </div>
        )}

        {state.kind === 'not_found' && (
          <Message theme={theme} title={t('companyDetails.not_found_title')} body={t('companyDetails.not_found_body')} />
        )}

        {state.kind === 'error' && (
          <Message theme={theme} title={t('companyDetails.page_title')} body={t('companyDetails.load_error')} />
        )}

        {state.kind === 'blocked' && (
          <Message
            theme={theme}
            title={t(`companyDetails.reason_${reasonKey(state.reason)}_title`)}
            body={t(`companyDetails.reason_${reasonKey(state.reason)}_body`)}
          />
        )}

        {state.kind === 'done' && (
          <div>
            <div
              className="inline-flex items-center gap-2.5 px-3 py-1.5"
              style={{
                background: theme.positiveSurface,
                border: `1px solid ${theme.positiveBorder}`,
                borderRadius: Math.min(theme.radius, 2),
                font: `600 11px/1 ${theme.fontFamily}`,
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                color: theme.positive,
              }}
            >
              <Check className="h-3.5 w-3.5" />
              {t('companyDetails.done_badge')}
            </div>
            <p
              className="mt-4"
              role="status"
              style={{ font: `400 17px/1.6 ${theme.fontFamily}`, color: theme.ink }}
              data-testid="company-details-done"
            >
              {state.sentTo
                ? t('companyDetails.done_body', { number: state.documentNumber || '', email: state.sentTo })
                : t('companyDetails.done_body_no_email', { number: state.documentNumber || '' })}
            </p>
          </div>
        )}

        {state.kind === 'form' && context && (
          <form onSubmit={handleSubmit} noValidate>
            <SmallCaps theme={theme}>{t('companyDetails.eyebrow')}</SmallCaps>
            <h1
              style={{
                margin: '12px 0 8px',
                font: `500 clamp(26px, 4.5vw, 34px)/1.15 ${theme.fontFamily}`,
                letterSpacing: '-.02em',
                color: theme.ink,
              }}
            >
              {t('companyDetails.page_title')}
            </h1>
            <p style={{ margin: '0 0 28px', font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted }}>
              {t('companyDetails.page_intro')}
            </p>

            <div
              className="mb-6 p-6"
              style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
            >
              <SmallCaps theme={theme}>{t('companyDetails.order_heading')}</SmallCaps>
              <div className="mt-4 flex flex-col gap-3">
                {merchantName && <Row theme={theme} label={t('companyDetails.merchant')} value={merchantName} />}
                {context.document_number && (
                  <Row theme={theme} label={t('companyDetails.document_number')} value={context.document_number} />
                )}
                {context.issued_at && (
                  <Row theme={theme} label={t('companyDetails.issued_at')} value={formatLongDate(context.issued_at, locale)} />
                )}
                {typeof context.amount === 'number' && context.currency && (
                  <Row
                    theme={theme}
                    label={t('companyDetails.amount')}
                    value={formatMoney(context.amount, context.currency, locale)}
                  />
                )}
              </div>
              <NoVatNote
                theme={theme}
                text={t('companyDetails.vat_note')}
                explainerHref={explainerHref}
                className="mt-4 pt-4"
              />
            </div>

            <div
              className="p-6"
              style={{ background: theme.surface, border: `1px solid ${theme.hairline}`, borderRadius: theme.radius }}
            >
              {formError && (
                <p className="mb-5" role="alert" style={{ font: `400 14px/1.5 ${theme.fontFamily}`, color: theme.accent }}>
                  {formError}
                </p>
              )}
              <CompanyDetailsFields
                theme={theme}
                values={company}
                onChange={updateField}
                errors={errors}
                venueCountry={merchantCountry}
                disabled={submitting}
              />
              <AccentButton
                theme={theme}
                type="submit"
                disabled={submitting}
                className="mt-6 w-full"
                style={{ padding: '16px', font: `600 16px/1 ${theme.fontFamily}` }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('companyDetails.submitting')}
                  </>
                ) : (
                  t('companyDetails.submit')
                )}
              </AccentButton>
            </div>
          </form>
        )}
      </main>

      <ShopFooter
        theme={theme}
        venueName={merchantName}
        venueAddress={venue?.location_address}
        legal={venue?.legal}
        locationId={context?.location_id || undefined}
      />
    </div>
  );
};

function reasonKey(reason: string): string {
  return (KNOWN_REASONS as string[]).includes(reason) ? reason : 'generic';
}

const Message: React.FC<{ theme: ShopTheme; title: string; body: string }> = ({ theme, title, body }) => (
  <div className="py-8" role="status">
    <h1 style={{ font: `500 26px/1.2 ${theme.fontFamily}`, color: theme.ink, margin: '0 0 8px' }}>{title}</h1>
    <p style={{ font: `400 15px/1.6 ${theme.fontFamily}`, color: theme.muted, margin: 0 }}>{body}</p>
  </div>
);

const Row: React.FC<{ theme: ShopTheme; label: string; value: string }> = ({ theme, label, value }) => (
  <div className="flex justify-between gap-4" style={{ font: `400 14px/1.4 ${theme.fontFamily}`, color: theme.body }}>
    <span className="min-w-0">{label}</span>
    <span className="text-right" style={{ color: theme.ink }}>
      {value}
    </span>
  </div>
);

export default CompanyDetailsPage;
